import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, FileText, Download, LogOut, Clock, Video, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [boegen, setBoegen] = useState([]);
  const [loading, setLoading] = useState(true);
  const name = localStorage.getItem("lw_portal_name") || "Kunde";
  const token = localStorage.getItem("lw_portal_token");

  useEffect(() => {
    if (!token) { navigate("/portal/login"); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [aptsRes, boegenRes] = await Promise.all([
        axios.get(`${API}/portal/appointments`, { headers }),
        axios.get(`${API}/portal/beratungsboegen`, { headers }),
      ]);
      setAppointments(aptsRes.data);
      setBoegen(boegenRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("lw_portal_token");
        localStorage.removeItem("lw_portal_name");
        navigate("/portal/login");
      }
    }
    setLoading(false);
  };

  const downloadPDF = async (bogenId, date) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/portal/beratungsboegen/${bogenId}/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Beratungsbogen_${date || "Download"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch { toast.error("Fehler beim Download"); }
  };

  const logout = () => {
    localStorage.removeItem("lw_portal_token");
    localStorage.removeItem("lw_portal_name");
    navigate("/portal/login");
  };

  const formatDate = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
    catch { return d; }
  };

  const today = new Date().toLocaleDateString("sv-SE");
  const nextAppointment = appointments.find(a => a.date >= today && a.status === "confirmed");
  const pastAppointments = appointments.filter(a => a.date < today || a.status === "completed");

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 font-['Manrope']">Mein Bereich</p>
              <p className="text-xs text-slate-500">Hallo, {name}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="portal-logout" className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors px-3 py-2 rounded-xl hover:bg-red-50 active:scale-95">
            <LogOut className="h-4 w-4" /> Abmelden
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Next appointment */}
        <section>
          <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-cyan-700" />
            Ihr nächster Termin
          </h2>

          {nextAppointment ? (
            <div className="bg-white rounded-2xl border border-cyan-200 p-6 shadow-sm" data-testid="next-appointment-card">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 bg-cyan-50 rounded-2xl flex items-center justify-center shrink-0">
                  <CalendarDays className="h-7 w-7 text-cyan-700" />
                </div>
                <div className="flex-1">
                  <p className="text-xl font-bold text-slate-900 font-['Manrope']">
                    {formatDate(nextAppointment.date)}
                  </p>
                  <p className="text-lg text-slate-600 mt-1 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    {nextAppointment.time_slot} Uhr
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs font-medium bg-green-50 text-green-700 px-3 py-1.5 rounded-full">
                      Bestätigt
                    </span>
                    {nextAppointment.consultation_type && (
                      <span className="text-xs font-medium bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full">
                        {nextAppointment.consultation_type === "erstberatung" ? "Erstberatung" : "Folgeberatung"}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Video join */}
              {nextAppointment.video_room && (
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-3">
                    Zum Termin treten Sie einfach über diesen Link bei:
                  </p>
                  <a
                    href={`/video/${nextAppointment.video_room}`}
                    data-testid="portal-video-join"
                    className="flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-4 text-base font-semibold transition-all active:scale-[0.98] w-full"
                  >
                    <Video className="h-5 w-5" />
                    Video-Beratung beitreten
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base text-slate-500 mb-4">Kein anstehender Termin</p>
              <a href="/#termin-buchen" className="inline-flex items-center gap-2 text-cyan-700 font-semibold hover:underline text-base">
                Neuen Termin buchen <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}
        </section>

        {/* Beratungsboegen */}
        <section>
          <h2 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            Ihre Beratungsbögen
          </h2>

          {boegen.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-base text-slate-500">
                Noch keine abgeschlossenen Beratungsbögen vorhanden.
              </p>
              <p className="text-sm text-slate-400 mt-2">
                Nach Ihrer Beratung finden Sie hier das Protokoll zum Download.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {boegen.map((b) => (
                <div
                  key={b.id}
                  data-testid={`portal-bogen-${b.id}`}
                  className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
                      <FileText className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-slate-800">
                        Beratung vom {b.consultation_date ? formatDate(b.consultation_date) : "—"}
                      </p>
                      <p className="text-sm text-slate-500">
                        Beratungseinsatz nach §37 Abs. 3 SGB XI
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => downloadPDF(b.id, b.consultation_date)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl h-12 px-5 text-sm font-semibold shrink-0 active:scale-95"
                    data-testid={`portal-download-${b.id}`}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Help */}
        <section className="bg-slate-100/50 rounded-2xl p-6 text-center">
          <p className="text-base text-slate-600 mb-3">Fragen oder einen neuen Termin buchen?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3 text-base font-semibold transition-all active:scale-[0.98]">
              <Phone className="h-5 w-5" /> Anrufen
            </a>
            <a href="/#termin-buchen" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3 text-base font-semibold transition-all active:scale-[0.98]">
              <CalendarDays className="h-5 w-5" /> Neuer Termin
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
